import React, { memo } from 'react';
import styled from 'styled-components';
import isNumber from 'lodash/isNumber';
import upperFirst from 'lodash/upperFirst';

import colors from '@front/common/src/utils/colors';
import { SEVERAL_RECURRENCE } from '@front/common/src/constants/cleaning';
import { WeeklyRecurrence } from '@front/common/src/types/commons';
import { getCurrentCountry } from '@front/common/src/utils/country';

import { MultiplePrestationsPricesCleaningProps } from '@front/customer/src/components/basket/types';
import { getFormattedPriceForMultiple } from '@front/customer/src/utils/cleaning';
import { CONFIG_BY_COUNTRY } from '@front/customer/src/constants/country-config';

import { translate } from '@front/i18n';

import Accordion from '@front/web-ui/src/components/accordion/accordion';
import Separator from '@front/web-ui/src/components/separator/separator';
import Text from '@front/web-ui/src/components/base-text/variants/text';
import Skeleton from '@front/web-ui/src/components/skeleton/skeleton';

import PromoCode from '../../../promo-code/promo-code';
import useBasketSection from '../../../../hooks/use-basket-section';
import {
  ANIMALS_PATH,
  CLEANING_OPTIONS_PATH,
  DURATION_PATH,
  FREQUENCY_PATH,
  PRESTATIONS_PATH,
  route,
} from '../../../../steps/steps';
import Category from '../common/category';
import Surcharges from '../common/surcharges';
import AccordionSubtitle from '../common/accordion-subtitle';

import MultiplePricesWithTax from './multiple-prices-with-tax';

const t = translate('basket.cleaning.services');

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 20px;
`;

const StyledAccordion = styled(Accordion)`
  margin-top: 20px;
`;

const MultiplePrestationsPrices = ({
  cleaningPrestation,
  cleaningPrestationOptions,
  duration,
  hasPets,
  isLoadingPrices,
  mode,
  prices,
  promotion,
  shouldShowPromoCode,
  universeReference,
  user,
  weeklyRecurrence,
  weeklyRecurrences,
}: MultiplePrestationsPricesCleaningProps) => {
  const { hasTaxCreditInBasket } = CONFIG_BY_COUNTRY[getCurrentCountry()];
  const recurrenceType =
    weeklyRecurrence && weeklyRecurrence >= 2
      ? SEVERAL_RECURRENCE
      : Object.keys(weeklyRecurrences).find((key) => weeklyRecurrences[key as WeeklyRecurrence] === weeklyRecurrence);
  const frequencyText = isNumber(weeklyRecurrence)
    ? t(`.frequency.recurrent.${recurrenceType}`, { weeklyRecurrence })
    : undefined;

  const hasPrestations = !!cleaningPrestation;
  const hasDuration = !!duration;
  const hasTaxCredit = prices && prices.tax_credit.price > 0;
  const formattedPriceAfterTax = getFormattedPriceForMultiple(hasDuration, prices?.after_tax);

  const { isOpen, isVisible } = useBasketSection({
    data: hasPrestations,
    sectionLocations: [
      route(PRESTATIONS_PATH),
      route(FREQUENCY_PATH),
      route(DURATION_PATH),
      route(CLEANING_OPTIONS_PATH),
      route(ANIMALS_PATH),
    ],
  });

  if (!isVisible) return null;

  return (
    <Container>
      <Separator />
      <StyledAccordion
        icon='basket'
        iconLabel={t('basket.services.icon_label.basket')}
        title={upperFirst(t('universes.cleaning.title'))}
        titlePreview={isLoadingPrices ? <Skeleton linesWidth={['110px']} /> : formattedPriceAfterTax}
        subtitle={
          hasTaxCreditInBasket ? (
            <AccordionSubtitle
              hasTaxCredit={hasTaxCredit}
              isLoading={isLoadingPrices}
              formattedPrice={getFormattedPriceForMultiple(hasDuration, prices?.total)}
            />
          ) : undefined
        }
        subtitleVisibility='close'
        isOpen={isOpen}
      >
        {hasPrestations ? (
          <>
            <Category
              cleaningPrestation={cleaningPrestation}
              cleaningPrestationOptions={cleaningPrestationOptions}
              duration={duration}
              hasPets={hasPets}
              frequencyText={frequencyText}
            />
            <Surcharges surcharges={prices?.surcharges} />
            <MultiplePricesWithTax
              hasDuration={hasDuration}
              hasPrestations={hasPrestations}
              isLoading={isLoadingPrices}
              prices={prices}
            />
          </>
        ) : (
          <Text.SM color={colors.ink50}>{t('.no_services')}</Text.SM>
        )}
      </StyledAccordion>
      {mode === 'creation' && hasPrestations && shouldShowPromoCode && (
        <PromoCode
          cleaningPrestation={cleaningPrestation}
          promotion={promotion}
          totalPrice={prices?.total.price || 0}
          universeReference={universeReference}
          user={user}
        />
      )}
    </Container>
  );
};

export default memo(MultiplePrestationsPrices);
